import { useEffect, useState } from 'react';
import { Status } from '@sunbit-dev/purchase-common/src/enums/guided-app/Status';
import { EventsNames } from '~/enums/Events';
import { loanApplicationNetworkService } from '~/services/LoanApplicationNetworkService';
import { setCustomer } from '~/store/reducers/customer/customer';
import { addError } from '~/store/reducers/errors/errors';
import { getUrlPathByStatus } from '~/utilities/navigation/navigation';
import * as purchaseStore from '~/store/reducers/purchase/purchase';
import { getWorkflowState } from '~/store/reducers/purchase/selectors';
import { getActiveRequirement } from '~/utilities/workflowState/workflowState';
import { RequirementName } from '~/models/WorkflowState';
import { useDispatch, useSelector } from 'react-redux';
import * as customerStore from '~/store/reducers/customer/customer';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import URLs from '~/enums/URLs';
import { ErrorCodes } from '~/enums/Errors';
import { useTrack } from '~/hooks/track';

export const useOnLogin = () => {
  const { purchaseInfo } = useSelector(purchaseStore.getStore);
  const workflowState = useSelector(getWorkflowState);
  const emailVerificationRequirement = getActiveRequirement(workflowState, RequirementName.EMAIL_VERIFICATION);
  const { isLoggedIn } = useSelector(customerStore.getStore);
  const [initialRouteByStatusTriggered, setInitialRouteByStatusTriggered] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const { search } = useLocation();
  const { track } = useTrack();

  useEffect(() => {
    if (isLoggedIn) {
      const onLogin = async () => {
        const parsedSearch = qs.parse(search, { ignoreQueryPrefix: true });
        const { cvid, customerVerified } = parsedSearch as {
          cvid: string;
          customerVerified: string;
        };

        if (search.toLowerCase().includes('cvid') && !cvid) {
          console.error('[useOnLogin] cvid present in URL but failed to parse', { rawSearch: search });
          track({
            eventName: 'cvid_parsing_error_login',
            props: { rawSearch: search, status: purchaseInfo?.status },
          });
        }

        const isCustomerVerified = customerVerified === '1';

        if (emailVerificationRequirement) {
          if (cvid) {
            let verifySucceeded = false;
            try {
              const { customer } = await loanApplicationNetworkService.verifyEmail({ cvid });
              await dispatch(setCustomer({ customer }));
              verifySucceeded = true;
            } catch (error) {
              await dispatch(addError({ eventName: EventsNames.emailVerify, payload: error }));
            }

            if (verifySucceeded) {
              try {
                const { status } = await loanApplicationNetworkService.getApplicationStatus({
                  loanApplicationId: purchaseInfo?.loanApplicationId,
                });
                if (status === Status.Agreement) {
                  try {
                    const offer = await loanApplicationNetworkService.getActiveOffer();
                    await dispatch(purchaseStore.setOffer({ offer }));
                    history.replace({ pathname: URLs.Agreement, search });
                  } catch (error) {
                    await dispatch(addError({ eventName: EventsNames.getActiveOffer, payload: error }));
                    history.replace({ pathname: URLs.OptionsLoading, search });
                  }
                } else {
                  history.replace({ pathname: URLs.OptionsLoading, search });
                }
              } catch (error) {
                await dispatch(addError({ eventName: EventsNames.getApplicationStatus, payload: error }));
              }
            }
          } else {
            history.replace({ pathname: URLs.EmailVerification, search });
          }
        } else if (isCustomerVerified && purchaseInfo?.status === Status.ExtraVerification) {
          history.replace({ pathname: URLs.CustomerVerified, search });
        } else if (purchaseInfo?.status === Status.ChargeFailed) {
          dispatch(addError({ eventName: EventsNames.chargeFailed, payload: { code: ErrorCodes.ChargeFailed } }));
        } else {
          // Switch to the last page user left
          history.replace({ pathname: getUrlPathByStatus({ status: purchaseInfo?.status }), search });
        }

        setInitialRouteByStatusTriggered(true);
      };
      onLogin();
    }
  }, [isLoggedIn]);

  return { initialRouteByStatusTriggered };
};
